import type { SearchParams } from './api';

/**
 * Search state lives in the URL (/buscar?city=...&specialty=...), so results
 * can be shared, bookmarked and reached from links on the home page.
 */
export function readSearch(params: URLSearchParams): SearchParams {
  const text = (key: string) => params.get(key)?.trim() || undefined;
  const mode = params.get('mode');
  const gender = params.get('gender');
  const maxPrice = Number(params.get('maxPrice'));
  const page = Number(params.get('page'));
  return {
    q: text('q'),
    city: text('city'),
    uf: text('uf')?.toUpperCase(),
    neighborhood: text('neighborhood'),
    mode: mode === 'online' || mode === 'presencial' ? mode : undefined,
    specialty: text('specialty'),
    gender: gender === 'feminino' || gender === 'masculino' ? gender : undefined,
    maxPriceCents: maxPrice > 0 ? Math.round(maxPrice * 100) : undefined,
    trial: params.get('trial') === '1' || undefined,
    verified: params.get('verified') === '1' || undefined,
    includeOnline: params.get('online') === '0' ? false : undefined,
    sort: params.get('sort') === 'price' ? 'price' : undefined,
    page: page > 1 ? page : undefined,
  };
}

export function writeSearch(search: SearchParams): string {
  const params = new URLSearchParams();
  const set = (key: string, value: string | number | undefined) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  };
  set('q', search.q);
  set('city', search.city);
  set('uf', search.uf);
  set('neighborhood', search.neighborhood);
  set('mode', search.mode);
  set('specialty', search.specialty);
  set('gender', search.gender);
  set('maxPrice', search.maxPriceCents ? Math.round(search.maxPriceCents / 100) : undefined);
  if (search.trial) params.set('trial', '1');
  if (search.verified) params.set('verified', '1');
  if (search.includeOnline === false) params.set('online', '0');
  set('sort', search.sort === 'price' ? 'price' : undefined);
  set('page', search.page && search.page > 1 ? search.page : undefined);
  const qs = params.toString();
  return qs ? `/buscar?${qs}` : '/buscar';
}

/** How many refinements (besides place and objective) are active: shown on the Filters button. */
export function activeFilterCount(search: SearchParams) {
  return [
    search.mode,
    search.neighborhood,
    search.gender,
    search.maxPriceCents,
    search.trial,
    search.verified,
    search.includeOnline === false ? true : undefined,
  ].filter(Boolean).length;
}

const UFS = new Set(
  'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' '),
);

/** "São Paulo, SP" / "São Paulo - SP" / "São Paulo" → city and state. */
export function parsePlace(text: string): { city?: string; uf?: string } {
  const match = text.trim().match(/^(.*?)[\s,/-]+([A-Za-z]{2})$/);
  if (match && match[1].trim().length >= 2 && UFS.has(match[2].toUpperCase())) {
    return { city: match[1].trim(), uf: match[2].toUpperCase() };
  }
  return text.trim() ? { city: text.trim() } : {};
}
